import { useState } from 'react';
import { useMutation } from 'react-query';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router';

import {
  getUserInfoByUserId,
  postCreateUser,
  putUpdateUserPersonal,
  putUpdateUserProfile
} from 'src/apis/Profiles/profile';
import { appStore, useAppStore } from 'src/context/AppStore';
import { TProfile, TUpdateUserPersonal } from 'src/types/profiles/profile.model';
import { PathURL } from 'src/utils/constants/path';
import useLanguage from '../Language/useLanguage';
import { HTTP_STATUS_VALIDATION } from 'src/utils/constants/base';
import useAuthen from '../Authenticate/authen';
import { getProfile } from 'src/apis/Authenticates/signin';
import toastHelper from 'src/components/Forms/Toastify';

function useUserProfile() {
  const {
    language: { profilePage, common }
  } = useLanguage();

  const { profile } = useAppStore();
  const { logOutClearData } = useAuthen();
  const navigate = useNavigate();
  const [isEdittingUserPersonal, setIsEdittingUserPersonal] = useState(false);
  const [errorValidation, setErrorValidation] = useState();
  const [dataUserInfo, setDataUserInfo] = useState<TProfile>();

  const mutationGetUserInfoById = useMutation((userId: number) => getUserInfoByUserId(userId), {
    onSuccess: async (response: any) => {
      if (response && response.succeeded) {
        const userInfo: TProfile = { ...response.data };
        userInfo.userId = response.data.id;
        setDataUserInfo(userInfo);
      } else {
        navigate(`${PathURL.notfound}`);
      }
    }
  });

  const mutationUpdatePersonal = useMutation((param: TUpdateUserPersonal) => putUpdateUserPersonal(param), {
    onSuccess: async (response: any) => {
      if (response && response.error) {
        const { error } = response;
        Swal.fire({
          icon: 'error',
          text: error.message
        });
        return;
      }

      if (response && response.status === HTTP_STATUS_VALIDATION) {
        const { errors } = response;
        setErrorValidation(errors);
        return;
      }
      setIsEdittingUserPersonal(false);

      profile.displayName = `${dataUserInfo?.firstName} ${dataUserInfo?.lastName}`;
      appStore.updateProfile({ ...profile, ...dataUserInfo });
      toastHelper.showSuccess('Tạo mới user thành công');
    },
    onError: (error: any) => {
      toastHelper.showSuccess(error.response);
    }
  });

  const mutationUpdateProfile = useMutation((param: TProfile) => putUpdateUserProfile(param), {
    onSuccess: async (response: any) => {
      if (response && response.error) {
        const { error } = response;
        toastHelper.showSuccess(error.message);

        return;
      }

      if (response && response.status === HTTP_STATUS_VALIDATION) {
        const { errors } = response;
        setErrorValidation(errors);
        return;
      }

      toastHelper.showSuccess('Cập nhật thông tin thành công');
    }
  });

  const mutationPostCreateUser = useMutation((param: TProfile) => postCreateUser(param), {
    onSuccess: async (response: any) => {
      if (response && !response.succeeded) {
        const { error } = response;
        Swal.fire({
          icon: 'error',
          text: error.message
        });
        return;
      }

      if (response && response.status === HTTP_STATUS_VALIDATION) {
        const { errors } = response;
        setErrorValidation(errors);
        return;
      }

      Swal.fire({
        icon: 'success',
        text: common.validation.successfully
      });
      navigate(`${PathURL.user}`);
    },
    onError: (error: any) => {
      Swal.fire({
        icon: 'error',
        text: error.response
      });
    }
  });
  const mutationCurrentUser = useMutation(
    () => {
      return getProfile();
    },
    {
      onSuccess: async (response: any) => {
        if (response && response.succeeded) {
          const userInfo: TProfile = { ...response.data };
          userInfo.userId = response.data.id;
          setDataUserInfo(userInfo);
          appStore.updateProfile(userInfo);
        } else {
          logOutClearData();
          navigate(`${PathURL.signin}`);
        }
      },
      onError: () => {
        logOutClearData();
        navigate(`${PathURL.signin}`);
      }
    }
  );

  async function updateUserPersonal(param: TUpdateUserPersonal) {
    setDataUserInfo({ ...param });
    await mutationUpdatePersonal.mutateAsync(param);
  }

  async function getUserInfosByUserId(userId: number) {
    await mutationGetUserInfoById.mutateAsync(userId);
  }

  return {
    profile,
    mutationUpdateProfile,
    mutationUpdatePersonal,
    updateUserPersonal,
    isEdittingUserPersonal,
    setIsEdittingUserPersonal,
    errorValidation,
    dataUserInfo,
    getUserInfosByUserId,
    mutationPostCreateUser,
    mutationCurrentUser
  };
}

export default useUserProfile;
